/**
 * Cross-side twist-witness corroboration (the scissor-gait kill,
 * 2026-07-12): on near-straight generated limbs, the grandchild direction's
 * perpendicular component — the twist witness reorient's ladder ranks
 * highest — is real geometry but can be AUTHORING noise pointing anywhere
 * (the goblin's right knee-bend witness sits ~122° from her left's mirror).
 * The per-bone floors in reorient.ts guard MEASUREMENT noise (import
 * tolerance) and are honest about it; they cannot see authoring noise,
 * because a single bone offers no second opinion.
 *
 * The second opinion is the ontology's own mirror structure: BBSA is
 * mirror-by-construction, so a rig's Left/Right witnesses for a mirror-pair
 * bone must agree ACROSS the rig's sagittal plane within their derived
 * measurement uncertainty. When they don't, the witnesses contradict the
 * one anatomical prior every biped rig asserts by having sides at all —
 * and BOTH lose standing (we cannot tell which side is junk: flag, don't
 * average, don't pick — ADR-004 §7's rule applied to reorientation).
 * Demotion goes to the ladder's next tier (canonical secondary), which is
 * declared same-kind-consistent anatomy on both sides by construction.
 *
 * The comparison is same-kind by definition (witness vs mirrored witness) —
 * it never compares a measured bend line against a declared canonical axis,
 * which are different semantic directions (a hyper-extended knee's bend
 * line points BACKWARD while its kneecap faces forward; reorient.ts already
 * refuses to mix those kinds).
 *
 * Thresholds are DERIVED, never rig-tuned: each witness direction carries
 * angular uncertainty ≈ (tolerance/‖grandchild offset‖ +
 * tolerance/‖child offset‖) / ‖perpendicular fraction‖ — positional noise
 * amplified by the shallowness of the bend — and a contradiction requires
 * separation beyond the two sides' summed uncertainty.
 *
 * Depends on: math/vec3, ir (types), retarget/convention.
 * Depended on by: retarget-clip (buildPairs pre-pass).
 */
import { vec3Cross, vec3Dot, vec3Length, vec3Normalize, vec3Scale, vec3Sub, type Vec3Data } from '../math/vec3.js';
import type { RigBone, RigDescriptor } from '../ir/rig-descriptor.js';
import type { Ontology } from '../ir/ontology.js';
import { conventionTriad } from './convention.js';

export interface WitnessContradiction {
  /** Which rig's witnesses disagreed. */
  readonly rig: string;
  readonly leftOntologyId: string;
  readonly rightOntologyId: string;
  readonly leftBone: string;
  readonly rightBone: string;
  /** Angle between the mirrored left witness and the right witness. */
  readonly separationRadians: number;
  /** The two sides' summed derived angular uncertainty. */
  readonly toleranceRadians: number;
}

export interface WitnessCorroboration {
  /** Ontology IDs whose grandchild witness lost standing on THIS rig. */
  readonly disqualified: ReadonlySet<string>;
  /** The loud record — one entry per contradicting mirror pair. */
  readonly contradictions: readonly WitnessContradiction[];
}

interface Witness {
  readonly direction: Vec3Data;
  readonly uncertaintyRadians: number;
  readonly bone: string;
}

/** The world-space twist witness of one bone, with derived uncertainty. */
function witnessOf(
  bone: RigBone,
  child: RigBone | undefined,
  grandchild: RigBone | undefined,
  tolerance: number,
): Witness | null {
  if (!child || !grandchild) return null;
  const primaryOffset = vec3Sub(child.worldBind.position, bone.worldBind.position);
  const grandOffset = vec3Sub(grandchild.worldBind.position, bone.worldBind.position);
  const primaryLength = vec3Length(primaryOffset);
  const grandLength = vec3Length(grandOffset);
  if (primaryLength <= tolerance || grandLength <= tolerance) return null;
  const primary = vec3Normalize(primaryOffset);
  const g = vec3Normalize(grandOffset);
  const perp = vec3Sub(g, vec3Scale(primary, vec3Dot(g, primary)));
  const perpLength = vec3Length(perp);
  const positionalNoise = tolerance / grandLength + tolerance / primaryLength;
  if (perpLength <= positionalNoise) return null; // reorient's own floor territory
  return {
    direction: vec3Normalize(perp),
    uncertaintyRadians: positionalNoise / perpLength,
    bone: bone.name,
  };
}

/**
 * Check every mapped ontology mirror pair on one rig. Returns the ontology
 * IDs whose grandchild witnesses must not pin twist, plus the loud record.
 */
export function corroborateTwistWitnesses(
  rig: RigDescriptor,
  byOntologyId: ReadonlyMap<string, RigBone>,
  ontology: Ontology,
  tolerance: number,
): WitnessCorroboration {
  const triad = conventionTriad(rig);
  const disqualified = new Set<string>();
  const contradictions: WitnessContradiction[] = [];
  if (triad === null) return { disqualified, contradictions };
  const n = vec3Normalize(triad.side);
  const mirror = (v: Vec3Data): Vec3Data => vec3Sub(v, vec3Scale(n, 2 * vec3Dot(v, n)));

  // Pairs come from the ontology's DECLARED mirror links (first-class,
  // involutive-validated — ADR-003 §2a), never derived from id spelling: a
  // valid ontology whose sided ids are not Left*/Right* must still be
  // protected (Codex, PR #22).
  for (const bone of ontology.byId.values()) {
    if (bone.side !== 'left' || bone.mirror === null) continue;
    const leftId = bone.id;
    const rightId = bone.mirror;

    const witnessFor = (id: string): Witness | null => {
      const bone = byOntologyId.get(id);
      if (!bone) return null;
      const childId = ontology.byId.get(id)?.primaryChild ?? null;
      const grandchildId = childId === null ? null : (ontology.byId.get(childId)?.primaryChild ?? null);
      if (childId === null || grandchildId === null) return null;
      return witnessOf(bone, byOntologyId.get(childId), byOntologyId.get(grandchildId), tolerance);
    };
    const left = witnessFor(leftId);
    const right = witnessFor(rightId);
    // A pair is only checkable when BOTH witnesses exist — a one-sided
    // witness has no second opinion and keeps the per-bone floors' verdict.
    if (left === null || right === null) continue;

    const separationRadians = Math.acos(
      Math.max(-1, Math.min(1, vec3Dot(mirror(left.direction), right.direction))),
    );
    const toleranceRadians = left.uncertaintyRadians + right.uncertaintyRadians;
    if (separationRadians > toleranceRadians) {
      disqualified.add(leftId);
      disqualified.add(rightId);
      contradictions.push({
        rig: rig.label,
        leftOntologyId: leftId,
        rightOntologyId: rightId,
        leftBone: left.bone,
        rightBone: right.bone,
        separationRadians,
        toleranceRadians,
      });
    }
  }
  return { disqualified, contradictions };
}

/**
 * A center bone's witnesses pointed to opposite sides of the body across the
 * two rigs — the spine's curvature, which is POSTURE, not a frame.
 */
export interface PostureWitnessContradiction {
  readonly ontologyId: string;
  readonly sourceBone: string;
  readonly targetBone: string;
  /** Angle between the two witnesses, each measured about its own bone from its rig's anatomical forward. */
  readonly separationRadians: number;
}

/**
 * Cross-RIG corroboration for the CENTER chain (Tripo rig presets,
 * 2026-09-23 — the "Meshy twist" on tiger-mixamo and tiger-actorcore).
 *
 * On a center bone the grandchild witness is the spine's curvature at that
 * level: Spine1's witness is where the neck sits relative to the Spine1 →
 * Spine2 segment. That is POSTURE — lordosis or kyphosis — and its sign is
 * per character, not a property of the bone's frame. The two-directions tier
 * pairs the source's witness with the target's as if they named the same
 * anatomical direction; when one spine curves forward and the other back at
 * the same level, the pairing rolls the bone a half-turn. Measured: the
 * Mixamo walk's Spine1 witness points FORWARD (5.3°), the Tripo presets'
 * BACKWARD (1.1–2.2°), and Spine1 played 180° off while Spine2 (both
 * forward) played clean — the cancelling half-turn a waist wring is made of.
 * Mirror corroboration cannot see this: a center bone has no mirror.
 *
 * The second opinion is each rig's own anatomical forward (hub +Z through
 * its declared/promoted convention). Each witness is expressed as a signed
 * angle about its own bone from forward; when the two land more than a
 * quarter-turn apart — on opposite sides of the body — AND beyond their
 * summed derived uncertainty, neither can pin the twist, and BOTH rigs'
 * witnesses for that bone drop to the canonical tier (forward-declared on
 * the whole center chain). Loud, like the mirror demotion.
 */
export function corroboratePostureWitnesses(
  source: RigDescriptor,
  sourceByOntologyId: ReadonlyMap<string, RigBone>,
  sourceTolerance: number,
  target: RigDescriptor,
  targetByOntologyId: ReadonlyMap<string, RigBone>,
  targetTolerance: number,
  ontology: Ontology,
): { disqualified: ReadonlySet<string>; contradictions: readonly PostureWitnessContradiction[] } {
  const disqualified = new Set<string>();
  const contradictions: PostureWitnessContradiction[] = [];
  const srcTriad = conventionTriad(source);
  const tgtTriad = conventionTriad(target);
  if (srcTriad === null || tgtTriad === null) return { disqualified, contradictions };

  /** The witness as a signed angle about the bone axis from the rig's forward, with its uncertainty. */
  const postureOf = (
    byOntologyId: ReadonlyMap<string, RigBone>,
    id: string,
    forward: Vec3Data,
    tolerance: number,
  ): { angle: number; uncertainty: number; bone: string } | null => {
    const bone = byOntologyId.get(id);
    const childId = ontology.byId.get(id)?.primaryChild ?? null;
    const grandchildId = childId === null ? null : (ontology.byId.get(childId)?.primaryChild ?? null);
    if (!bone || childId === null || grandchildId === null) return null;
    const child = byOntologyId.get(childId);
    const w = witnessOf(bone, child, byOntologyId.get(grandchildId), tolerance);
    if (w === null || !child) return null;
    const axis = vec3Normalize(vec3Sub(child.worldBind.position, bone.worldBind.position));
    const f = vec3Sub(forward, vec3Scale(axis, vec3Dot(forward, axis)));
    if (vec3Length(f) < 0.5) return null; // bone too near forward to name a side of the body
    const fp = vec3Normalize(f);
    const lateral = vec3Normalize(vec3Cross(axis, fp));
    return {
      angle: Math.atan2(vec3Dot(w.direction, lateral), vec3Dot(w.direction, fp)),
      uncertainty: w.uncertaintyRadians,
      bone: w.bone,
    };
  };

  for (const bone of ontology.byId.values()) {
    if (bone.side !== 'center') continue;
    const s = postureOf(sourceByOntologyId, bone.id, srcTriad.facing, sourceTolerance);
    const t = postureOf(targetByOntologyId, bone.id, tgtTriad.facing, targetTolerance);
    if (s === null || t === null) continue;
    const raw = Math.abs(s.angle - t.angle) % (2 * Math.PI);
    const separationRadians = raw > Math.PI ? 2 * Math.PI - raw : raw;
    if (separationRadians > Math.PI / 2 && separationRadians > s.uncertainty + t.uncertainty) {
      disqualified.add(bone.id);
      contradictions.push({ ontologyId: bone.id, sourceBone: s.bone, targetBone: t.bone, separationRadians });
    }
  }
  return { disqualified, contradictions };
}
